/**
 * The two ways into a programme's team: `/programme-teams`, where the account
 * chooses a programme, and `/programmes/:programmeId/team`, fixed to one.
 *
 * The tenant-wide entry is a list beside the team it selects, and the
 * selection lives in the URL, so a team is addressable and the narrow layout
 * has a real destination to go back to.
 *
 * @module router/programmes/ProgrammeTeamsRoutes
 */

import React from "react";

import { useNavigate, useParams } from "react-router";

import { programmeFilterDefs } from "@/components/shared/programme-filter-defs";
import { AssignmentManagement } from "@/components/teams/AssignmentManagement";
import { manageableProgrammes } from "@/components/teams/manageable-programmes";
import { ProgrammePicker } from "@/components/teams/ProgrammePicker";
import {
  Alert,
  Button,
  EmptyState,
  FilterPanel,
  Heading,
  MasterDetail,
  PageHeader,
  useCollectionFilters,
} from "@/components/ui";
import { useWorkspaceOutletContext } from "@/router/workspace-context";

/** The workspace read failed, so nothing below it can be stated as fact. */
function WorkspaceReadFailed({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => Promise<void>;
}): React.ReactElement {
  return (
    <Alert tone="warning" data-testid="programme-teams-error">
      <span>The programmes could not be loaded. {error.message}</span>
      <Button
        size="sm"
        icon="arrow-clockwise"
        onClick={() => {
          onRetry().catch(() => undefined);
        }}
        data-testid="programme-teams-retry"
      >
        Try again
      </Button>
    </Alert>
  );
}

function TeamDetail({
  programmeId,
  allowLeadTeamManagement,
  showUnmanaged = false,
  named = true,
}: {
  programmeId: string;
  allowLeadTeamManagement: boolean;
  /** Show a team the account cannot manage read-only, rather than refusing it. */
  showUnmanaged?: boolean;
  /** Head the team with the programme's name, which a page of its own already shows. */
  named?: boolean;
}): React.ReactElement {
  const {
    client,
    session,
    platformProgrammes,
    platformUsers,
    platformDataError,
    refreshPlatformWorkspace,
    editorSync,
  } = useWorkspaceOutletContext();
  // A team change can move the programme itself (the first builder starts
  // development), and an editor left on the old revision refuses its next save.
  const onChanged = async (): Promise<void> => {
    await refreshPlatformWorkspace();
    if (editorSync.record?.id === programmeId) {
      await editorSync.reloadProgramme();
    }
  };
  // The address is not a permission. `programme:read` is granted for every
  // programme in the tenant, so without this an id typed or followed into
  // either address loaded a team the account may not manage, armed every
  // control, and refused each write one error banner at a time.
  const manageable = manageableProgrammes(session, platformProgrammes, allowLeadTeamManagement);
  // A read that never answered has the same empty list as an account that
  // manages nothing, and telling the second story over the first sends the
  // reader looking for a permission they already hold.
  if (platformDataError) {
    return <WorkspaceReadFailed error={platformDataError} onRetry={refreshPlatformWorkspace} />;
  }
  const managed = manageable.some((programme) => programme.id === programmeId);
  if (!managed && !showUnmanaged) {
    return (
      <EmptyState
        icon="lock-key"
        title="You cannot manage this programme's team"
        description="Choose a programme you lead or administer."
        data-testid="programme-team-denied"
      />
    );
  }
  return (
    <AssignmentManagement
      client={client}
      session={session}
      programmes={platformProgrammes}
      users={platformUsers}
      fixedProgrammeId={programmeId}
      allowLeadTeamManagement={allowLeadTeamManagement}
      onChanged={onChanged}
      readOnly={!managed}
      named={named}
    />
  );
}

export interface ProgrammeTeamsBrowserProps {
  /** Where a team lives: the selection is `${base}/${programmeId}`. */
  base: string;
  /** Opens the first team on wide screens, for a screen that is nothing but this. */
  autoSelectFirst?: boolean;
}

/**
 * The programmes the account manages beside the team of the one selected,
 * with the selection in the URL so a team is addressable and the narrow layout
 * has a real destination to go back to.
 */
export function ProgrammeTeamsBrowser({
  base,
  autoSelectFirst = false,
}: ProgrammeTeamsBrowserProps): React.ReactElement {
  const navigate = useNavigate();
  const { programmeId } = useParams<{ programmeId: string }>();
  const { session, platformProgrammes, allowLeadTeamManagement } = useWorkspaceOutletContext();
  const manageable = manageableProgrammes(session, platformProgrammes, allowLeadTeamManagement);
  const { rows, filtering } = useCollectionFilters(
    manageable,
    programmeFilterDefs({
      workflow: "programme-teams-workflow-filter",
      level: "programme-teams-level-filter",
      discipline: "programme-teams-discipline-filter",
      text: "programme-teams-text-filter",
    }),
    { label: "Filter programmes", noun: "programme" },
  );

  return (
    <MasterDetail
      label="Programmes"
      selected={Boolean(programmeId)}
      onBack={() => {
        navigate(base);
      }}
      backLabel="All programmes"
      autoSelectFirst={
        autoSelectFirst
          ? {
              firstId: rows[0]?.id,
              onSelect: (id) => {
                navigate(`${base}/${id}`, { replace: true });
              },
            }
          : undefined
      }
      data-testid="programme-teams"
      masterHeader={<FilterPanel filtering={filtering} variant="flush" />}
      master={
        <ProgrammePicker
          programmes={rows}
          selectedId={programmeId}
          onSelect={(id) => {
            navigate(`${base}/${id}`);
          }}
        />
      }
      detail={
        programmeId ? (
          <TeamDetail programmeId={programmeId} allowLeadTeamManagement={allowLeadTeamManagement} />
        ) : (
          <EmptyState
            density="pane"
            icon="users-three"
            title="Choose a programme"
            description="The team, permissions and module assignments for the programme appear here."
            data-testid="programme-teams-no-selection"
          />
        )
      }
    />
  );
}

/** `/programme-teams` and `/programme-teams/:programmeId`. */
export function ProgrammeTeamsRoute(): React.ReactElement {
  const {
    session,
    platformProgrammes,
    platformDataError,
    allowLeadTeamManagement,
    refreshPlatformWorkspace,
  } = useWorkspaceOutletContext();
  const manageable = manageableProgrammes(session, platformProgrammes, allowLeadTeamManagement);

  return (
    <div className="flex flex-col gap-6" data-testid="programme-teams-route">
      <PageHeader
        title="Programme teams"
        description="See who works on each programme and which modules they build."
      />
      {platformDataError && (
        <WorkspaceReadFailed error={platformDataError} onRetry={refreshPlatformWorkspace} />
      )}
      {!platformDataError && manageable.length === 0 && (
        <Alert tone="info">No programmes are available for Programme teams.</Alert>
      )}
      {!platformDataError && manageable.length > 0 && (
        <ProgrammeTeamsBrowser base="/programme-teams" />
      )}
    </div>
  );
}

/** `/programmes/:programmeId/team`, fixed to the programme in the URL. */
export function ProgrammeTeamRoute(): React.ReactElement {
  const { programmeId } = useParams<{ programmeId: string }>();
  const { allowLeadTeamManagement } = useWorkspaceOutletContext();
  return (
    <div className="flex flex-col gap-6" data-testid="programme-team-route">
      <Heading level={2} className="sr-only">
        Programme team
      </Heading>
      <TeamDetail
        programmeId={programmeId ?? ""}
        allowLeadTeamManagement={allowLeadTeamManagement}
        showUnmanaged
        named={false}
      />
    </div>
  );
}
